<script lang="ts">
const __DsBlock = {
	type: 'input',
	icon: 'lucide:toggle-right',
	name: '$t:blocks.input_switch.name',
	description: '$t:blocks.input_switch.description',
	category: 'input',
	fieldBinding: { supportedTypes: ['boolean'] },

	options: [
		{
			name: 'modelValue',
			model: true,
		},
		{
			name: 'label',
		},
		{
			name: 'description',
		},
		{
			name: 'color',
			type: 'enum',
			values: BLOCK_COLOR_OPTIONS,
		},
		{
			name: 'size',
			type: 'enum',
			values: BLOCK_CONTROL_SIZE_OPTIONS,
		},
		{
			name: 'checkedIcon',
			block: BuiltinBlockIds.InputIcon,
		},
		{
			name: 'uncheckedIcon',
			block: BuiltinBlockIds.InputIcon,
		},
		{
			name: 'loading',
		},
		{
			name: 'disabled',
		},
		{
			name: 'disabledNotice',
		},
		{
			name: 'readonly',
		},
	],
}
</script>
<script lang="ts" setup>
import type { SwitchProps } from '@nuxt/ui';
import type { InputBlockProps } from '~/types/input-block';
import { reactivePick } from '@vueuse/core';
import { BuiltinBlockIds } from 'virtual:monospace-builtin-block-ids';
import { defineBlock } from '#layers/block-engine';
import { BLOCK_COLOR_OPTIONS, BLOCK_CONTROL_SIZE_OPTIONS } from '#layers/blocks/constants/ui-options';
import { useDisabledNotice, useReadonly } from '#layers/ui';
import { usePrimitiveFormField } from '~/composables/form-block/use-primitive-form-field';

interface InputSwitchBlockProps extends
	InputBlockProps,
	Pick<SwitchProps, 'color' | 'size' | 'description' | 'label' | 'loading' | 'loadingIcon' | 'checkedIcon' | 'uncheckedIcon' | 'required'> {
}

const props = defineProps<InputSwitchBlockProps>();
const model = defineModel<boolean>({
	default: undefined,
});

;

const forwarded = reactivePick(props, 'color', 'size', 'description', 'label', 'loading', 'loadingIcon', 'checkedIcon', 'uncheckedIcon', 'required');
const { modelValue, disabled, disabledNotice } = usePrimitiveFormField(props, { model });
const { showDisabledNotice } = useDisabledNotice({ disabled, disabledNotice });
const { preventKeyboardNavigation, preventPointer } = useReadonly(() => props.readonly ?? false);
</script>

<template>
	<DTooltip :delay-duration="0" :disabled="!showDisabledNotice" :text="disabledNotice" :content="{ align: 'center' }">
		<div class="w-min!">
			<DSwitch
				v-bind="forwarded"
				v-model="modelValue"
				:disabled
				:aria-readonly="readonly"
				:class="{ 'pointer-events-none': readonly }"
				@pointerdown.capture="preventPointer"
				@click.capture="preventPointer"
				@keydown.capture="preventKeyboardNavigation"
			/>
		</div>
	</DTooltip>
</template>
